import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowLeft, Users, Trash2, Plus, Save, Loader2, ChevronRight, Building2,
} from "lucide-react";

/**
 * Data Gathering page — first-page (Members) mirrored from kinntegraa.club.
 *
 * Route: /leads/:leadId/introduction
 *
 * Users land here when they click the "Introduction" capsule on a lead row
 * or right after creating a new lead. Only the Members tab is wired here
 * (the deeper Income/Expenses/Goals workflow still lives on kinntegraa.club
 * — the "Next Step → Data Gathering" button bridges the sub-broker there).
 */

const RELATIONSHIP_OPTIONS = ["Self", "Spouse", "Son", "Daughter", "Father", "Mother", "Brother", "Sister", "Other"];
const TAX_STATUS_OPTIONS = ["Resident", "NRI with Indian Passport", "NRI with Foreign Passport", "Foreign Passport"];
const TAX_REGIME_OPTIONS = ["Old Regime", "New Regime", "NA"];
const TAX_SLAB_OPTIONS = ["0%", "5%", "10%", "15%", "20%", "25%", "30%"];
const PROCEED_OPTIONS = ["Data Gathering", "KYC Information", "Account Opening"];

export default function LeadIntroductionPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/lead-mgmt/${leadId}`);
        setLead(data.data);
      } catch (e) {
        toast.error("Failed to load lead: " + (e?.response?.data?.detail || e.message));
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [leadId]);

  const fmtLeadDate = (iso) => {
    if (!iso) return "—";
    const s = iso.length >= 10 ? iso.slice(0, 10) : iso;
    const [y, m, d] = s.split("-");
    const day = parseInt(d, 10);
    if (!day || !y || !m) return iso;
    const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sept","Oct","Nov","Dec"];
    const month = MONTHS[parseInt(m, 10) - 1] || m;
    const suf = (n) => {
      const v = n % 100;
      if (v >= 11 && v <= 13) return "th";
      switch (n % 10) { case 1: return "st"; case 2: return "nd"; case 3: return "rd"; default: return "th"; }
    };
    return `${day}${suf(day)} ${month}, ${y}`;
  };

  if (loading) {
    return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-teal-600" size={32}/></div>;
  }
  if (!lead) {
    return <div className="p-10 text-center text-gray-400">Lead not found.</div>;
  }

  return (
    <div className="px-4 py-6" data-testid="lead-intro-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/leads")}
            className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 hover:bg-gray-100"
            aria-label="Back to Leads"
            data-testid="intro-back-btn"
          ><ArrowLeft size={16}/></button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Introduction</h1>
            <p className="text-xs text-gray-500 mt-0.5">Collect financial information for {lead.first_name} {lead.last_name}</p>
          </div>
        </div>
        <div className="text-xs text-gray-500">
          Lead Date: <span className="text-gray-800 font-medium">{fmtLeadDate(lead.lead_date)}</span>
        </div>
      </div>

      {/* Section tabs removed per user — only Members section is shown */}

      <Card className="border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <MembersSection lead={lead} onSaved={setLead}/>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Members section ─────────────────────────────────────────
function MembersSection({ lead, onSaved }) {
  const { api, user, isSubBroker } = useAuth();
  const navigate = useNavigate();
  const intro = lead.introduction || {};
  const [associates, setAssociates] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/lead-mgmt/lookups/associates");
        setAssociates(data?.data || []);
      } catch (_) {}
    })();
    // eslint-disable-next-line
  }, []);

  const seededPrimary = {
    id: 1,
    name: `${lead.first_name || ""} ${lead.last_name || ""}`.trim(),
    dob: lead.dob || "",
    relation: "Self",
    father_name: "",
    life_expectancy: "",
    retirement_year: "",
    tax_status: "",
    tax_regime: "",
    tax_slab: "",
    is_primary: true,
  };
  const [members, setMembers] = useState(() => {
    const src = intro.members || [];
    if (!src.length) return [seededPrimary];
    return src.map((m, i) => ({
      id: i + 1,
      name: m.name || "",
      dob: m.dob || "",
      relation: m.relation || (i === 0 ? "Self" : ""),
      father_name: m.father_name || "",
      life_expectancy: m.life_expectancy != null ? String(m.life_expectancy) : "",
      retirement_year: m.retirement_year != null ? String(m.retirement_year) : "",
      tax_status: m.tax_status || "",
      tax_regime: m.tax_regime || "",
      tax_slab: m.tax_slab || "",
      is_primary: !!m.is_primary || (src.length === 1 && i === 0),
    }));
  });
  const [isIndividual, setIsIndividual] = useState(intro.is_non_individual === false ? false : (intro.is_individual !== false));
  const [isNonIndividual, setIsNonIndividual] = useState(!!intro.is_non_individual);
  const [activeMemberId, setActiveMemberId] = useState(() => {
    const src = intro.members || [];
    return src.length ? 1 : 1;
  });
  // Non-Individual: companies list
  const [companies, setCompanies] = useState(() => {
    const src = intro.companies || [];
    if (!src.length) return [{
      id: 1, name: "", date_of_incorporation: "", tax_status: "", tax_slab: "",
      auth_persons: [],
    }];
    return src.map((c, i) => ({
      id: i + 1,
      name: c.name || "",
      date_of_incorporation: c.date_of_incorporation || "",
      tax_status: c.tax_status || "",
      tax_slab: c.tax_slab || "",
      auth_persons: Array.isArray(c.auth_persons) ? c.auth_persons
                    : [c.auth_person_1, c.auth_person_2].filter(Boolean),
    }));
  });
  const [activeCompanyId, setActiveCompanyId] = useState(1);
  const [openAPMenu, setOpenAPMenu] = useState(null); // company id whose AP dropdown is open
  const updC = (id, k, v) => setCompanies(prev => prev.map(c => c.id === id ? { ...c, [k]: v } : c));
  const toggleAP = (id, name) => setCompanies(prev => prev.map(c => {
    if (c.id !== id) return c;
    const arr = c.auth_persons || [];
    return { ...c, auth_persons: arr.includes(name) ? arr.filter(n => n !== name) : [...arr, name] };
  }));
  const addCompany = () => setCompanies(prev => {
    const newId = Math.max(...prev.map(x => x.id), 0) + 1;
    setTimeout(() => setActiveCompanyId(newId), 0);
    return [...prev, {
      id: newId, name: "", date_of_incorporation: "", tax_status: "", tax_slab: "",
      auth_persons: [],
    }];
  });
  const removeCompany = (id) => setCompanies(prev => {
    const next = prev.filter(c => c.id !== id);
    if (activeCompanyId === id && next.length) setTimeout(() => setActiveCompanyId(next[0].id), 0);
    return next.length ? next : prev;
  });
  const [mfdRiaPartner, setMfdRiaPartner] = useState(() => {
    if (isSubBroker) return String(user?.id || user?._id || lead.associate_id || "");
    return intro.mfd_ria_partner || lead.associate_id || "";
  });
  const [proceedOption, setProceedOption] = useState(intro.proceed_option || "");
  const [busy, setBusy] = useState(false);

  // Sub-brokers can only assign themselves — filter the list to just their own record.
  const associateOptions = (isSubBroker
    ? associates.filter(a => String(a.id) === String(user?.id || user?._id))
    : associates
  ).map(a => {
    const isRealCode = a.code && /^\d/.test(a.code);
    return {
      value: a.id,
      label: isRealCode ? `${a.code} - ${a.name}` : (a.name || a.code || ""),
      search: `${a.code || ""} ${a.name || ""}`.toLowerCase(),
    };
  });

  const primaryName = (members.find(m => m.is_primary)?.name || "").trim();
  const familyName = intro.family_name || "";
  const computedFamilyName = primaryName ? `${primaryName} & Family` : "";

  const upd = (id, k, v) => setMembers(prev => prev.map(m => m.id === id ? { ...m, [k]: v } : m));
  const updM = (id, patch) => setMembers(prev => prev.map(m => m.id === id ? { ...m, ...patch } : m));
  const addMember = () => setMembers(prev => {
    const newId = Math.max(...prev.map(x => x.id), 0) + 1;
    setTimeout(() => setActiveMemberId(newId), 0);
    return [...prev, {
      id: newId,
      name: "", dob: "", relation: "", father_name: "",
      life_expectancy: "", retirement_year: "",
      tax_status: "", tax_regime: "", tax_slab: "", is_primary: false,
    }];
  });
  const removeMember = (id) => setMembers(prev => {
    const next = prev.filter(m => m.id !== id);
    if (activeMemberId === id && next.length) setTimeout(() => setActiveMemberId(next[0].id), 0);
    return next;
  });
  const setPrimary = (id) => setMembers(prev => prev.map(m => ({
    ...m,
    is_primary: m.id === id,
    relation: m.id === id ? "Self" : (m.relation === "Self" ? "Spouse" : m.relation),
  })));

  const save = async () => {
    // The introduction validation only requires primary holder for Individual accounts.
    // Non-Individual accounts (companies) don't need family members.
    if (isIndividual) {
      const primary = members.find(m => m.is_primary);
      if (!primary?.name?.trim()) return toast.error("Primary holder name is required");
    }
    if (isNonIndividual) {
      const firstCo = companies[0];
      if (!firstCo?.name?.trim()) return toast.error("Company / Entity name is required");
    }
    if (!isIndividual && !isNonIndividual) return toast.error("Select Individual or Non Individual under Introducing");
    setBusy(true);
    try {
      const payload = {
        family_name: computedFamilyName || `${lead.first_name} & Family`,
        mfd_ria_partner: mfdRiaPartner,
        proceed_option: proceedOption,
        is_individual: isIndividual,
        is_non_individual: isNonIndividual,
        companies: isNonIndividual ? companies.map(c => ({
          name: c.name,
          date_of_incorporation: c.date_of_incorporation || null,
          tax_status: c.tax_status || null,
          tax_slab: c.tax_slab || null,
          auth_persons: c.auth_persons || [],
        })) : [],
        members: isIndividual ? members.map(m => ({
          name: m.name,
          dob: m.dob || null,
          relation: m.relation || null,
          father_name: m.father_name || null,
          life_expectancy: m.life_expectancy === "" ? null : parseInt(m.life_expectancy),
          retirement_year: m.retirement_year === "" ? null : parseInt(m.retirement_year),
          tax_status: m.tax_status || null,
          tax_regime: m.tax_regime || null,
          tax_slab: m.tax_slab || null,
          is_primary: !!m.is_primary,
        })) : [],
      };
      const { data } = await api.post(`/lead-mgmt/${lead.id}/introduction`, payload);
      toast.success("Family details saved.");
      onSaved(data.data);

      if (proceedOption === "Data Gathering") {
        try {
          const { data: br } = await api.post(`/lead-mgmt/${lead.id}/bridge/data-gathering`);
          try { sessionStorage.setItem("pendingLeadForFamily", JSON.stringify(br.prefill || {})); } catch (_) {}
          toast(br.auto_login ? "Opening Data Gathering — auto-signed in." : `Opening Data Gathering. ${br.note || "Please sign in if prompted."}`,
                { duration: 6000 });
          window.open(br.redirect_url, "_blank", "noopener,noreferrer");
        } catch (e) {
          toast.error("Bridge to Data Gathering failed: " + (e?.response?.data?.detail || e.message));
        }
      } else if (proceedOption === "KYC Information") {
        navigate(`/leads/${lead.id}/kyc-information`);
      } else if (proceedOption === "Account Opening") {
        navigate(`/leads/${lead.id}/account-opening`);
      }
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      {/* Sub Broker Name + Family Name (stacked, matches kinntegraa.club) */}
      <div className="space-y-4 max-w-xl">
        <div>
          <label className="text-sm text-gray-600 mb-2 block">
            Sub Broker Name <span className="text-rose-500">*</span>
          </label>
          <SearchableSelect
            value={mfdRiaPartner}
            onChange={setMfdRiaPartner}
            options={associateOptions}
            placeholder="Select Sub Broker"
            testid="mfd-ria-select"
            disabled={isSubBroker}
          />
        </div>
        <div>
          <label className="text-sm text-gray-600 mb-2 block">Family Name</label>
          <input
            value={familyName}
            readOnly
            placeholder="Auto-generated from primary holder"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-700"
          />
        </div>
      </div>

      {/* Account Type + Family Details header */}
      <div>
        <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-3">
          <Users size={15}/> Introducing
        </label>
        <div className="flex items-center gap-6 mb-6">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={isIndividual}
                   onChange={(e) => setIsIndividual(e.target.checked)}
                   data-testid="acc-type-individual"
                   className="h-4 w-4 accent-[#1a7f6f]"/>
            <span className="text-sm text-gray-800">Individual</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={isNonIndividual}
                   onChange={(e) => setIsNonIndividual(e.target.checked)}
                   data-testid="acc-type-non-individual"
                   className="h-4 w-4 accent-[#1a7f6f]"/>
            <span className="text-sm text-gray-800">Non Individual</span>
          </label>
        </div>

        {/* Family Members grid — INDIVIDUAL only (kinntegraa.club style) */}
        {isIndividual && (
        <div className="border-t pt-5">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2"><Users size={15}/> Family Members</label>
            <button
              type="button"
              onClick={addMember}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
              data-testid="intro-add-member"
            ><Plus size={14}/> Add Member</button>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="hidden lg:grid gap-2 px-4 py-3 bg-gray-50 text-[11px] font-semibold uppercase tracking-wider text-gray-500 border-b"
                 style={{ gridTemplateColumns: '1.5fr 1.3fr 1fr 1fr 0.7fr 0.7fr 1.1fr 1fr 0.8fr 0.5fr 0.5fr' }}>
              <span className="min-w-0">Name</span>
              <span className="min-w-0">Father Name</span>
              <span className="min-w-0">DOB</span>
              <span className="min-w-0">Relation</span>
              <span className="min-w-0">Life Exp.</span>
              <span className="min-w-0">Ret. Year</span>
              <span className="min-w-0">Tax Status</span>
              <span className="min-w-0">Tax Regime</span>
              <span className="min-w-0">Tax Slab</span>
              <span className="min-w-0 text-center">Primary</span>
              <span className="min-w-0 text-center">Action</span>
            </div>
            <div className="divide-y divide-gray-100">
              {members.map((m) => {
                const foreign = m.tax_status === "Foreign Passport" || m.tax_status === "NRI with Foreign Passport";
                const setTaxStatus = (v) => {
                  if (v === "Foreign Passport" || v === "NRI with Foreign Passport") {
                    updM(m.id, { tax_status: v, tax_regime: "NA", tax_slab: "0%" });
                  } else {
                    upd(m.id, "tax_status", v);
                  }
                };
                const inp = "w-full min-w-0 h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500";
                const sel = "w-full min-w-0 h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500";
                return (
                  <div key={m.id}
                       className="grid grid-cols-1 lg:grid gap-2 px-4 py-3 items-center"
                       style={{ gridTemplateColumns: '1.5fr 1.3fr 1fr 1fr 0.7fr 0.7fr 1.1fr 1fr 0.8fr 0.5fr 0.5fr' }}
                       data-testid={`intro-member-row-${m.id}`}>
                    <input value={m.name} onChange={(e) => upd(m.id, "name", e.target.value)}
                           placeholder="Enter name" className={inp}
                           data-testid={`intro-name-${m.id}`}/>
                    <input value={m.father_name || ""} onChange={(e) => upd(m.id, "father_name", e.target.value)}
                           placeholder="Father name" className={inp}
                           data-testid={`intro-father-${m.id}`}/>
                    <input type="date" value={m.dob || ""} onChange={(e) => upd(m.id, "dob", e.target.value)}
                           className={inp} data-testid={`intro-dob-${m.id}`}/>
                    {m.is_primary ? (
                      <input value="Self" readOnly className={`${inp} bg-gray-50 text-gray-700`}/>
                    ) : (
                      <select value={m.relation || ""} onChange={(e) => upd(m.id, "relation", e.target.value)}
                              className={sel} data-testid={`intro-relation-${m.id}`}>
                        <option value="">Select</option>
                        {RELATIONSHIP_OPTIONS.filter(r => r !== "Self").map(r => <option key={r}>{r}</option>)}
                      </select>
                    )}
                    <input type="number" min="50" max="120" placeholder="Years"
                           value={m.life_expectancy} onChange={(e) => upd(m.id, "life_expectancy", e.target.value)}
                           className={inp} data-testid={`intro-lifeexp-${m.id}`}/>
                    <input type="number" min="2020" max="2100" placeholder="Year"
                           value={m.retirement_year} onChange={(e) => upd(m.id, "retirement_year", e.target.value)}
                           className={inp} data-testid={`intro-retyear-${m.id}`}/>
                    <select value={m.tax_status || ""} onChange={(e) => setTaxStatus(e.target.value)}
                            className={sel} data-testid={`intro-taxstatus-${m.id}`}>
                      <option value="">Select</option>
                      {TAX_STATUS_OPTIONS.map(o => <option key={o}>{o}</option>)}
                    </select>
                    <select value={foreign ? "NA" : (m.tax_regime || "")} onChange={(e) => upd(m.id, "tax_regime", e.target.value)}
                            disabled={foreign}
                            className={`${sel} ${foreign ? "bg-gray-100" : ""}`}
                            data-testid={`intro-taxregime-${m.id}`}>
                      <option value="">Select</option>
                      {TAX_REGIME_OPTIONS.map(o => <option key={o}>{o}</option>)}
                    </select>
                    <select value={foreign ? "0%" : (m.tax_slab || "")} onChange={(e) => upd(m.id, "tax_slab", e.target.value)}
                            disabled={foreign}
                            className={`${sel} ${foreign ? "bg-gray-100" : ""}`}
                            data-testid={`intro-taxslab-${m.id}`}>
                      <option value="">Select</option>
                      {TAX_SLAB_OPTIONS.map(o => <option key={o}>{o}</option>)}
                    </select>
                    <div className="min-w-0 flex justify-center">
                      <input type="radio" name={`primary-${lead.id}`}
                             checked={m.is_primary} onChange={() => setPrimary(m.id)}
                             className="h-4 w-4 accent-[#1a7f6f] cursor-pointer"
                             data-testid={`intro-primary-${m.id}`}/>
                    </div>
                    <div className="min-w-0 flex justify-center">
                      {!m.is_primary && (
                        <button onClick={() => removeMember(m.id)}
                                className="text-rose-500 hover:bg-rose-50 rounded-md p-1.5"
                                aria-label="Remove member">
                          <Trash2 size={14}/>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        )}

        {/* Non-Individual: Company Details */}
        {isNonIndividual && (
          <div className="border-t pt-5">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Building2 size={15}/> Company Details
              </label>
              <button
                type="button"
                onClick={addCompany}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
                data-testid="intro-add-company"
              ><Plus size={14}/> Add Company</button>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="hidden lg:grid gap-2 px-4 py-3 bg-gray-50 text-[11px] font-semibold uppercase tracking-wider text-gray-500 border-b"
                   style={{ gridTemplateColumns: '1.8fr 1fr 1.3fr 0.9fr 1.8fr 0.5fr' }}>
                <span className="min-w-0">Company Name</span>
                <span className="min-w-0">DOI</span>
                <span className="min-w-0">Entity Type</span>
                <span className="min-w-0">Tax Slab</span>
                <span className="min-w-0">Authorised Persons</span>
                <span className="min-w-0 text-center">Action</span>
              </div>
              <div className="divide-y divide-gray-100">
                {companies.map((c) => {
                  const inp = "w-full min-w-0 h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500";
                  const sel = "w-full min-w-0 h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500";
                  return (
                    <div key={c.id}
                         className="grid grid-cols-1 lg:grid gap-2 px-4 py-3 items-center"
                         style={{ gridTemplateColumns: '1.8fr 1fr 1.3fr 0.9fr 1.8fr 0.5fr' }}
                         data-testid={`intro-company-row-${c.id}`}>
                      <input value={c.name || ""} onChange={(e) => updC(c.id, "name", e.target.value)}
                             placeholder="Enter company" className={inp}
                             data-testid={`intro-co-name-${c.id}`}/>
                      <input type="date" value={c.date_of_incorporation || ""}
                             onChange={(e) => updC(c.id, "date_of_incorporation", e.target.value)}
                             className={inp} data-testid={`intro-co-doi-${c.id}`}/>
                      <select value={c.tax_status || ""} onChange={(e) => updC(c.id, "tax_status", e.target.value)}
                              className={sel} data-testid={`intro-co-taxstatus-${c.id}`}>
                        <option value="">Select</option>
                        <option>Company</option>
                        <option>Partnership Firm</option>
                        <option>LLP</option>
                        <option>HUF</option>
                        <option>Trust</option>
                        <option>Sole Proprietorship</option>
                        <option>Association of Persons</option>
                        <option>Body of Individuals</option>
                        <option>Other</option>
                      </select>
                      <select value={c.tax_slab || ""} onChange={(e) => updC(c.id, "tax_slab", e.target.value)}
                              className={sel} data-testid={`intro-co-taxslab-${c.id}`}>
                        <option value="">Select</option>
                        {TAX_SLAB_OPTIONS.map(o => <option key={o}>{o}</option>)}
                      </select>
                      {/* Authorised Persons multi-select */}
                      <div className="relative min-w-0">
                        <button
                          type="button"
                          onClick={() => setOpenAPMenu(openAPMenu === c.id ? null : c.id)}
                          className={`${inp} flex items-center justify-between text-left`}
                          data-testid={`intro-co-ap-${c.id}`}
                        >
                          <span className={`truncate ${(c.auth_persons || []).length ? "text-gray-800" : "text-gray-400"}`}>
                            {(c.auth_persons || []).length
                              ? (c.auth_persons.length <= 2
                                 ? c.auth_persons.join(", ")
                                 : `${c.auth_persons.slice(0, 2).join(", ")} +${c.auth_persons.length - 2}`)
                              : "Select persons"}
                          </span>
                          <ChevronRight size={14} className={`ml-2 flex-shrink-0 text-gray-400 transition ${openAPMenu === c.id ? "rotate-90" : ""}`}/>
                        </button>
                        {openAPMenu === c.id && (
                          <div className="absolute z-20 mt-1 w-64 max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg py-1">
                            {members.filter(m => m.name).length === 0 && (
                              <div className="px-3 py-2 text-xs text-gray-400">Add family members first</div>
                            )}
                            {members.filter(m => m.name).map(m => {
                              const checked = (c.auth_persons || []).includes(m.name);
                              return (
                                <label key={m.id}
                                       className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                                  <input type="checkbox" checked={checked}
                                         onChange={() => toggleAP(c.id, m.name)}
                                         className="h-4 w-4 accent-[#1a7f6f]"
                                         data-testid={`intro-co-ap-opt-${c.id}-${m.id}`}/>
                                  <span>{m.name}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex justify-center">
                        {companies.length > 1 && (
                          <button onClick={() => removeCompany(c.id)}
                                  className="text-rose-500 hover:bg-rose-50 rounded-md p-1.5"
                                  aria-label="Remove company"
                                  data-testid={`intro-co-remove-${c.id}`}>
                            <Trash2 size={14}/>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer: Next Step + Save & Next */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600">Next Step:</label>
          <select
            value={proceedOption}
            onChange={(e) => setProceedOption(e.target.value)}
            className="w-64 h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            data-testid="intro-next-step"
          >
            <option value="">Select next step</option>
            {PROCEED_OPTIONS.map(o => <option key={o}>{o}</option>)}
          </select>
        </div>
        <button
          onClick={save} disabled={busy}
          className="inline-flex items-center gap-2 rounded-md bg-[#1a7f6f] hover:bg-[#166a5c] text-white text-sm font-semibold px-8 py-2 disabled:opacity-60"
          data-testid="stage2-save-btn"
        >
          {busy ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>}
          Save &amp; Next
        </button>
      </div>
    </div>
  );
}

function SearchableSelect({ value, onChange, onBlur, options, placeholder, testid, error, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); onBlur?.(); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onBlur]);
  const selected = (options || []).find(o => o.value === value);
  const filtered = useMemo(() => {
    const needle = q.toLowerCase().trim();
    if (!needle) return options || [];
    return (options || []).filter(o => (o.search || o.label.toLowerCase()).includes(needle));
  }, [options, q]);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-md text-sm bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:text-gray-400 ${
          error ? "border-rose-400 focus:ring-rose-400" : "border-gray-300 focus:ring-teal-500"
        }`}
        data-testid={testid}
      >
        <span className={selected ? "text-gray-900" : "text-gray-400"}>
          {selected ? selected.label : (placeholder || "Select")}
        </span>
        <ChevronRight size={14} className={`text-gray-400 transition-transform ${open ? "rotate-90" : ""}`}/>
      </button>
      {open && !disabled && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="p-2 border-b sticky top-0 bg-white">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type to filter…"
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
              data-testid={`${testid}-search`}
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-gray-400">No matches</div>
            )}
            {filtered.map(o => (
              <button
                type="button"
                key={o.value}
                onClick={() => { onChange(o.value); setOpen(false); setQ(""); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-teal-50 ${o.value === value ? "bg-teal-100 font-medium text-teal-800" : "text-gray-700"}`}
              >{o.label}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
